import { Box, Card, CardContent, Grid2 as Grid, useMediaQuery, useTheme } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  ConditionScoreIcon,
  ContextScoreIcon,
} from '@/components/dashboard/navigation/SidebarIcons';
import { COLORS } from '@/components/dashboard/scoring';
import ContextScoreCard from '@/components/home/cards/ContextScoreCard';
import HomeScoreCard from '@/components/home/cards/HomeScoreCard';
import InspectionSummaryVisualCard from '@/components/home/cards/InspectionSummaryVisualCard';
import { InspectionCarouselOverlay } from '@/components/inspection/InspectionCarouselOverlay';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';

const ReadMoreBlurb = ({
  title,
  text,
  icon,
}: {
  title: string;
  text: string;
  icon?: React.ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: COLORS.text,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
        {icon}
        {title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--tx-muted)',
          lineHeight: 1.7,
          ...(!expanded
            ? {
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical' as const,
                overflow: 'hidden',
              }
            : {}),
        }}>
        {text}
      </div>
      {text.length > 120 && (
        <div
          data-testid={`summary-readmore-${title.toLowerCase().replace(/\s+/g, '-')}`}
          onClick={() => setExpanded(!expanded)}
          style={{
            fontSize: 12,
            color: 'var(--tx-muted)',
            cursor: 'pointer',
            marginTop: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}>
          {expanded ? 'Show Less' : 'Read More'}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}>
            <ChevronDown size={14} />
          </span>
        </div>
      )}
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AssessmentSummaryCard = ({
  conditionNarrative,
  contextNarrative,
  nhdNarrative,
}: {
  conditionNarrative: string;
  contextNarrative: string;
  nhdNarrative: string | null;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Card
      data-testid="summary-assessment-card"
      sx={{ bgcolor: COLORS.card, borderRadius: '24px' }}>
      <CardContent sx={{ p: '20px' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: COLORS.text, marginBottom: 0 }}>
          Assessment summary
        </div>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '10px' : '20px',
          }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ReadMoreBlurb
              title="The Home"
              text={conditionNarrative}
              icon={<ConditionScoreIcon size={14} />}
            />
          </Box>
          {!isMobile && <div style={{ width: 1, background: COLORS.border, flexShrink: 0 }} />}
          {isMobile && <div style={{ height: 1, background: COLORS.border, flexShrink: 0 }} />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ReadMoreBlurb
              title="The Area"
              text={contextNarrative}
              icon={<ContextScoreIcon size={14} />}
            />
          </Box>
          {nhdNarrative && (
            <>
              {!isMobile && <div style={{ width: 1, background: COLORS.border, flexShrink: 0 }} />}
              {isMobile && <div style={{ height: 1, background: COLORS.border, flexShrink: 0 }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ReadMoreBlurb title="NHD Disclosure" text={nhdNarrative} />
              </Box>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// The Home and The Area share the first screen with HomeDNA on a computer: the window's height less the frame above
// (20px gutter, the 60px property header, 20px) and HomeDNA below (20px, its ~170px card, the 20px bottom gutter), held
// above a floor where the radar stays readable. On a 27" the row keeps growing so the cards and HomeDNA fill the window
// exactly (a laptop never reaches the old 640 ceiling, so this changes nothing below that)
const SCORE_ROW_HEIGHT = 'clamp(400px, calc(100dvh - 336px), 1100px)';

// 27": HomeDNA takes 60px more height for taller ticks, and the score cards give up exactly that much
const fitsTallOverview = () => window.innerWidth >= 1680 && window.innerHeight >= 1000;

export const SummaryView = () => {
  const { selectedProperty } = usePropertiesContext();
  const [tallOverview, setTallOverview] = useState(fitsTallOverview);
  useEffect(() => {
    const onResize = () => setTallOverview(fitsTallOverview());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const scoreRowHeight = tallOverview ? 'calc(100dvh - 396px)' : SCORE_ROW_HEIGHT;
  const { cardMode } = useDashboardLayout();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { conditionScoreExplanation, contextScoreExplanation, isLoading } = useHauserEQResults(
    selectedProperty?.property.id,
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const conditionNarrative = conditionScoreExplanation ?? (isLoading ? '' : 'Data not available');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const contextNarrative = contextScoreExplanation ?? (isLoading ? '' : 'Data not available');

  // Carousel state mirrors HomeHQ — clicking a HomeDNA bar opens the
  // InspectionCarouselOverlay scoped to that issue for quick view.
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselInitialIssueId, setCarouselInitialIssueId] = useState<string | null>(null);

  const openCarouselForIssue = (issueId: string | null) => {
    setCarouselInitialIssueId(issueId);
    setCarouselOpen(true);
  };

  // Mobile: simple stacked layout (no Grid overhead). Desktop: Grid for 2-col score cards.
  if (isMobile) {
    return (
      <Box
        data-testid="summary-view"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          pt: 0,
          pb: '10px',
          px: '10px',
        }}>
        <HomeScoreCard cardMode={cardMode} />
        <ContextScoreCard cardMode={cardMode} />
        <InspectionSummaryVisualCard
          listPrice={selectedProperty?.property.price}
          cardMode={cardMode}
          onIssueSelect={(issue) => openCarouselForIssue(issue.id)}
        />
        {/* Hidden for now — LLM content is redundant with the visual summary
        <AssessmentSummaryCard
          conditionNarrative={conditionNarrative}
          contextNarrative={contextNarrative}
          nhdNarrative={null}
        />
        */}
        <InspectionCarouselOverlay
          open={carouselOpen}
          onClose={() => setCarouselOpen(false)}
          initialIssueId={carouselInitialIssueId}
        />
      </Box>
    );
  }

  return (
    <Box data-testid="summary-view" sx={{ p: 0 }}>
      <Grid container spacing={'20px'}>
        <Grid size={{ md: 6 }} sx={{ height: scoreRowHeight }}>
          <HomeScoreCard cardMode={cardMode} />
        </Grid>
        <Grid size={{ md: 6 }} sx={{ height: scoreRowHeight }}>
          <ContextScoreCard cardMode={cardMode} />
        </Grid>
        <Grid size={12}>
          <InspectionSummaryVisualCard
            listPrice={selectedProperty?.property.price}
            cardMode={cardMode}
            tall={tallOverview}
            onIssueSelect={(issue) => openCarouselForIssue(issue.id)}
          />
        </Grid>
        {/* Hidden for now — LLM content is redundant with the visual summary
        <Grid size={12}>
          <AssessmentSummaryCard
            conditionNarrative={conditionNarrative}
            contextNarrative={contextNarrative}
            nhdNarrative={null}
          />
        </Grid>
        */}
      </Grid>
      <InspectionCarouselOverlay
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        initialIssueId={carouselInitialIssueId}
      />
    </Box>
  );
};
